import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { DateTime } from 'luxon';
import { FeUtil, BeUtil } from '../../../../hezky-routes';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-trip-map',
  templateUrl: './trip-map.component.html',
  styleUrl: './trip-map.component.scss',
})

export class TripMapComponent implements OnChanges {
  @Input({ required: true }) trip!: any;
  @Input({ required: true }) lastLeg!: any;
  @Input({ required: true }) sequencedLegs!: any[];
  @Input({ required: true }) isViewReport!: boolean;
  @Input({ required: true }) showCapabilities!: string[];
  @Input({ required: false }) isDoorOpen!: boolean;

  @Output() onRefresh = new EventEmitter<'REFRESH'>();
  @Output() onGenerateReport = new EventEmitter<boolean>();

  isLoading:boolean = false;
  zoom = 12;
  center!: google.maps.LatLngLiteral;
  path: google.maps.LatLngLiteral[] = [];
  waypointData: google.maps.LatLngLiteral[] = [];
  private map: google.maps.Map | null = null;
  private polyline: google.maps.Polyline | null = null;
  startMarkerContent!: HTMLElement;
  endMarkerContent!: HTMLElement;
  polylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#1976D2',
    strokeOpacity: 0.8,
    strokeWeight: 3,
    zIndex: 1,
    geodesic: true,
    clickable: false,
    draggable: false,
    icons: [{
      icon: {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 2,
        strokeColor: '#000',
        strokeWeight: 2,
        fillColor: '#000',
        fillOpacity: 1.0
      },
      offset: '100%',
      repeat: '500px'
    }]
  };
  mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: google.maps.ControlPosition.BOTTOM_LEFT,
    },
    mapTypeControl: false,
    zoomControl: false,
    cameraControl:false,
    scrollwheel: true,
    mapId: 'cb5fe97220ce0418469f7b27',
    maxZoom: 14,
    minZoom: 5,
  };
  dropPoints: any[] = [];
  encodedOverViewPolyline: string | null = null;
  alarmTemplate: { threshold_on: string, threshold_condition: any }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    this.startMarkerContent = this.createMarkerElement('#4CAF50', 10, 'start');
    this.endMarkerContent = this.createMarkerElement('#2196F3', 10, 'end');
    if (changes['trip']?.currentValue) {
      if (!this.isViewReport) {
        this.trip?.alarm_templates?.forEach((template: any) => {
          this.alarmTemplate.push({
            threshold_on: template[0].threshold_on,
            threshold_condition: this.feUtil.groupByKey(template, 'threshold_condition')
          });
        });
      }
      let legFilters = this.trip?.status == 'discard' ? ['active', 'paused', 'completed', 'discard'] : ['inactive', 'active', 'paused', 'completed'];
      this.sequencedLegs = this.sequencedLegs.filter((leg: any) => legFilters.includes(leg.status));
      this.sequencedLegs.unshift({ location: this.trip?.sender_location, isSender: true, start_date: this.trip?.start_date });
      this.lastLeg = this.sequencedLegs[this.sequencedLegs.length - 1];
      this.dropPoints = this.sequencedLegs
        .filter((leg: any) => leg?.location?.latitude && leg?.location?.longitude)
        .map((leg: any) => ({
          lat: Number(leg.location.latitude),
          lng: Number(leg.location.longitude),
          timestamp: leg.end_date || null
        }))
        .filter(point =>
          !isNaN(point.lat) && !isNaN(point.lng) &&
          point.lat !== null && point.lng !== null
        );
      this.fetchTripCoordinates();
    }
  }

  protected onMapReady(map: google.maps.Map): void {
    this.map = map;
    this.updatePolyline();
  }

  private updatePolyline(): void {
    if (!this.map || !this.path.length) return;

    if (this.polyline) {
      this.polyline.setMap(null);
    }

    this.polyline = new google.maps.Polyline({
      path: this.path,
      map: this.map,
      ...this.polylineOptions,
    });
  }

  protected updateMapCenter(): void {
    if (this.path.length > 0) {
      const lastPoint = this.path[this.path.length - 1];
      this.center = { lat: lastPoint.lat, lng: lastPoint.lng };
    }
  }

  private createMarkerElement(color: string, size: number, type: string): HTMLElement {
    const element = document.createElement('div');
    element.className = `marker-content-${type}`;
    element.style.width = `${size}px`;
    element.style.height = `${size}px`;
    element.style.backgroundColor = color;
    element.style.borderRadius = '50%';
    element.style.border = '1px solid white';
    element.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    element.style.position = 'relative';
    element.style.zIndex = '1';
    return element;
  }

  protected fetchTripCoordinates(): void {
    this.isLoading = true;
    const params = { timezone: this.feUtil.time_zone };
    this.beUtil.tripsSvc.getTripCoordinates(this.lastLeg?.['leg_id'], params)
    .pipe(untilDestroyed(this), finalize(() => this.isLoading = false))
      .subscribe({
      next: async ({ status, data }: { status: number, data: any }) => {
        if(status !== 200) return;
        this.path = data?.coordinates?.sort((a: any, b: any) => {
          const aTime = DateTime.fromISO(a.timestamp).toMillis();
          const bTime = DateTime.fromISO(b.timestamp).toMillis();
          return aTime - bTime;
        }).map((loc: any) => ({ lat: loc.latitude, lng: loc.longitude }));
        this.updatePolyline();
        this.updateMapCenter();
        this.encodedOverViewPolyline = this.buildOverviewPolyline(this.path);
        this.isLoading = false;
      }
    });
  }

  private downsamplePath(points: google.maps.LatLngLiteral[], step = 5): google.maps.LatLngLiteral[] {
    if (points.length <= 2) return points;
    const result = points.filter((_, i) => i % step === 0);
    // Ensure last point is included
    if (result[result.length - 1] !== points[points.length - 1]) {
      result.push(points[points.length - 1]);
    }
    return result;
  }

  protected zoomIn(): void {
    if (this.map) {
      const currentZoom = this.map.getZoom();
      if (currentZoom !== undefined && currentZoom < 20) {
        this.map.setZoom(currentZoom + 1);
        this.zoom = currentZoom + 1;
        this.updateMapCenter();
      }
    }
  }

  protected zoomOut(): void {
    if (this.map) {
      const currentZoom = this.map.getZoom();
      if (currentZoom !== undefined && currentZoom > 1) {
        this.map.setZoom(currentZoom - 1);
        this.zoom = currentZoom - 1;
      }
    }
  }

  protected recenter(): void {
    if (this.map) {
      this.map.panTo(this.center);
    }
  }

  private buildOverviewPolyline(points: google.maps.LatLngLiteral[], maxLength = 7500): string {
    const toLatLng = (p: google.maps.LatLngLiteral) => new google.maps.LatLng(p.lat, p.lng);
    let step = 5;
    let sampled = this.downsamplePath(points, step);
    let encoded = google.maps.geometry.encoding.encodePath(sampled.map(toLatLng));
    while (encoded.length > maxLength && sampled.length > 2) {
      step = Math.ceil(step * 1.5);
      sampled = this.downsamplePath(points, step);
      encoded = google.maps.geometry.encoding.encodePath(sampled.map(toLatLng));
    }
    return encoded;
  }

  constructor(protected feUtil: FeUtil, protected beUtil: BeUtil) { }
}